/**
 * Les deux décisions d'index du visionneur de pages, isolées du rendu.
 *
 * Le parent pilote `imageIndex`, le composant tient `currentIndex`, et chacun
 * renvoyait à l'autre ce qu'il venait d'en recevoir : changer de page relançait
 * un défilement, dont chaque frame re-rapportait un index, qui re-rendait le
 * parent, qui re-commandait un défilement. Au-dessus, le Portal d'une modale
 * pose un setState en phase de commit à chaque rendu, donc la chaîne dépassait
 * les 50 mises à jour imbriquées et React jetait (#185) — ce qui démontait la
 * room LiveKit et sortait le tuteur de sa séance.
 *
 * Les deux règles qui cassent le cycle :
 *   - une valeur venue du parent n'est une COMMANDE que si elle est nouvelle,
 *     jamais quand c'est l'écho de ce qu'on vient de lui rapporter ;
 *   - les frames d'un défilement qu'on a soi-même demandé ne sont pas un geste
 *     de l'utilisateur, donc elles ne se rapportent pas.
 */

// Partagés plutôt que réalloués : `resolveScrollIndex` tourne à chaque frame de
// défilement, et les appelants ne font que déstructurer le résultat.
const NO_COMMAND = { apply: false, index: null }
const NOTHING = { index: null, arrived: false }

/**
 * @returns {{apply: boolean, index: number|null}} `apply` = déclencher le
 *          défilement et adopter l'index ; sinon ne rien faire.
 */
export function resolveParentCommand({
    imageIndex,
    handledIndex,
    currentIndex,
}) {
    // Le parent ne pilote pas (mode non contrôlé).
    if (imageIndex == null) return NO_COMMAND

    // Déjà traitée — c'est notre propre écho qui revient.
    if (imageIndex === handledIndex) return NO_COMMAND

    // Nouvelle, mais on y est déjà : on la retient sans re-défiler, sinon le
    // défilement repart alors que rien n'a bougé.
    if (imageIndex === currentIndex) return { apply: false, index: imageIndex }

    return { apply: true, index: imageIndex }
}

/**
 * @returns {{index: number|null, arrived: boolean}} `index` = nouvel index à
 *          adopter (null = rien à faire), `arrived` = le défilement demandé
 *          est terminé.
 */
export function resolveScrollIndex({
    offset,
    itemWidth,
    currentIndex,
    pendingTarget,
}) {
    // Largeur inconnue : l'index n'est pas calculable, et un NaN ferait
    // défiler la liste n'importe où.
    if (!itemWidth || !Number.isFinite(offset)) return NOTHING

    const index = Math.round(offset / itemWidth)

    // Un défilement qu'on a demandé : ses frames intermédiaires ne sont pas un
    // geste. On attend l'arrivée, sans rien rapporter en chemin.
    if (pendingTarget != null) {
        return index === pendingTarget
            ? { index: null, arrived: true }
            : NOTHING
    }

    return index === currentIndex ? NOTHING : { index, arrived: false }
}
